'use client';

/**
 * CardStack — generic single-slot swipeable card stack (pinned reminders,
 * course carousels). A single item renders plainly with no chrome; 2+ items
 * get prev/next arrows, a dot indicator, and touch-swipe support.
 */
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type TouchEvent,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Swipe must clear this many px before it counts as a navigation gesture
 *  (vs. a tap or an incidental touchmove). */
const SWIPE_THRESHOLD_PX = 40;

export interface CardStackProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
}

export function CardStack<T>({
  items,
  renderItem,
  className,
}: CardStackProps<T>) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  // Reset to the first card whenever the content changes — when Sara pins a
  // refreshed result (a new `items` array), the carousel should show the top
  // option, not silently hold the position the user had swiped to on the
  // PREVIOUS result. Keyed on the `items` reference, which is stable across a
  // swipe (only the local `index` moves) and changes only on a fresh pin, so
  // this never fights an in-progress swipe. Clamping alone (below) would keep
  // a mid-stack position on refresh; this is the content-based reset.
  useEffect(() => {
    setIndex(0);
  }, [items]);

  if (items.length === 0) return null;
  if (items.length === 1) {
    return <div className={className}>{renderItem(items[0], 0)}</div>;
  }

  // Clamp in case `items` shrinks out from under a stale index.
  const clampedIndex = Math.min(index, items.length - 1);
  const atStart = clampedIndex === 0;
  const atEnd = clampedIndex === items.length - 1;

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(items.length - 1, i + 1));

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) {
      if (delta > 0) goPrev();
      else goNext();
    }
    touchStartX.current = null;
  };

  return (
    <div className={cn('flex flex-col items-center gap-[12px]', className)}>
      <div
        className="w-full"
        data-testid="card-stack-viewport"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {renderItem(items[clampedIndex], clampedIndex)}
      </div>

      {/* Visually hidden; announces position for screen readers since the
          swipe/arrow navigation has no other text cue. */}
      <span className="sr-only" aria-live="polite">
        Card {clampedIndex + 1} of {items.length}
      </span>

      <div className="flex items-center gap-[16px]">
        <button
          type="button"
          onClick={goPrev}
          disabled={atStart}
          aria-label="Previous card"
          className={cn(
            // Color transition merged with the shared press feel's transform
            // (see PRESSABLE's doc comment in design-tokens.ts).
            'flex size-[38px] items-center justify-center rounded-full border-[1px] bg-white transition-[color,transform] duration-[160ms] ease-out motion-safe:active:scale-[0.96]',
            atStart
              ? 'cursor-not-allowed border-[#ece6dc] text-[#d8cdbd]'
              : 'border-[#ece6dc] text-[#E36001]'
          )}
        >
          <ChevronLeft className="size-4" />
        </button>

        <div className="flex items-center gap-1.5">
          {items.map((_, i) => (
            <span
              key={i}
              data-testid="card-stack-dot"
              data-active={i === clampedIndex}
              className={cn(
                'rounded-[3px] transition-all',
                i === clampedIndex
                  ? 'h-[6px] w-5 bg-[#E36001]'
                  : 'h-[6px] w-[6px] bg-[#e3d5c2]'
              )}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={goNext}
          disabled={atEnd}
          aria-label="Next card"
          className={cn(
            // Color transition merged with the shared press feel's transform
            // (see PRESSABLE's doc comment in design-tokens.ts).
            'flex size-[38px] items-center justify-center rounded-full border-[1px] bg-white transition-[color,transform] duration-[160ms] ease-out motion-safe:active:scale-[0.96]',
            atEnd
              ? 'cursor-not-allowed border-[#ece6dc] text-[#d8cdbd]'
              : 'border-[#ece6dc] text-[#E36001]'
          )}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  );
}
